import type { FunctionComponent } from 'react'
import React, { useEffect, useRef, useState } from 'react'
import {
  FacebookRoundelIcon,
  MailRoundelIcon,
  WhatsappRoundelIcon,
  XRoundelIcon,
} from '@which/seatbelt/src/components/Icons/Roundel'

import classnames from 'classnames'

import type { SharingData } from '../../../../generated/frontend'
import { ShareIcon } from '../../../../shared/components/ShareIcon'
import { buildSafeRel, sanitizeUrl } from '../../../../shared/utils/safe-url-attributes'
import { Save } from '../Save'
import styles from './StickyArticleActions.module.scss'

const ALLOWED_SHARE_LINK_PROTOCOLS = ['http:', 'https:', 'mailto:']

const SOCIAL_LINKS = [
  { key: 'facebook', Icon: FacebookRoundelIcon, label: 'Share this article on Facebook' },
  { key: 'twitter', Icon: XRoundelIcon, label: 'Share this article on X' },
  { key: 'mail', Icon: MailRoundelIcon, label: 'Share this article by email' },
  { key: 'whatsapp', Icon: WhatsappRoundelIcon, label: 'Share this article on WhatsApp' },
] as const

export const StickyArticleActions: FunctionComponent<Props> = ({ sharingData, className }) => {
  const [isShareOpen, setIsShareOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isShareOpen) {
      return
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsShareOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isShareOpen])

  if (!sharingData) {
    return null
  }

  const sanitize = (url: string) => sanitizeUrl(url, ALLOWED_SHARE_LINK_PROTOCOLS) ?? ''

  const links: Record<(typeof SOCIAL_LINKS)[number]['key'], string> = {
    facebook: sanitize(sharingData.facebook),
    twitter: sanitize(sharingData.twitter),
    mail: sanitize(sharingData.mail),
    whatsapp: sanitize(sharingData.whatsapp),
  }

  const safeRel = buildSafeRel(undefined, true)

  return (
    <div
      className={classnames(styles.stickyArticleActions, className)}
      data-testid="sticky-article-actions"
      ref={wrapperRef}
    >
      <Save location="sticky" variant="icon" />

      <button
        type="button"
        className={classnames(styles.shareToggle, {
          [styles.shareToggleOpen]: isShareOpen,
        })}
        onClick={() => setIsShareOpen((open) => !open)}
        aria-expanded={isShareOpen}
        aria-label="Share this article"
        data-testid="share-toggle-button"
        data-which-id="share-expand-button"
        data-item-text="share icon"
        data-item-spec="top"
      >
        <ShareIcon />
      </button>

      {isShareOpen && (
        <div className={styles.shareOptions} data-testid="sticky-share-options">
          {SOCIAL_LINKS.filter(({ key }) => links[key]).map(({ key, Icon, label }) => (
            <a
              key={key}
              href={links[key]}
              target="_blank"
              rel={safeRel}
              aria-label={label}
              className={styles.socialIcon}
              data-testid={`sticky-share-${key}`}
              data-which-id={`social-share-${key}-top`}
            >
              <Icon width={32} height={32} />
            </a>
          ))}
        </div>
      )}
    </div>
  )
}

type Props = {
  sharingData?: SharingData | null
  className?: string
}
